import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Type } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Footer } from "../components/Footer";
import { CompactHeader } from "../components/CompactHeader";
import * as Constants from "../../constants";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";

// QR codes on filed documents link to /search/{document ref}.
//   Case documents:  "{suit or reference number}/HC/{line id}"   e.g. REF1541142026/HC/108401
//   Affidavits:      "LSJ/HC/AFF/{upload id}/{year}"
// A case document goes straight to the Online Case Search results for that case;
// affidavits are verified on this page.
const CASE_DOC_REF = /^(.+)\/HC\/\d+$/i;
const AFFIDAVIT_REF = /^LSJ\/HC\/AFF\//i;

const decodeRef = (value) => {
  let v = (value || "").trim();
  // the ref is URL-encoded once in the QR link; decode until stable (handles double encoding)
  for (let i = 0; i < 3; i++) {
    try {
      const d = decodeURIComponent(v);
      if (d === v) break;
      v = d;
    } catch {
      break;
    }
  }
  return v.replace(/^\/+|\/+$/g, "");
};

// "REF1541142026/HC/108401" -> "REF1541142026"; null when it is not a case document ref
const caseRefFromDocRef = (ref) => {
  if (!ref || AFFIDAVIT_REF.test(ref)) return null;
  const m = ref.match(CASE_DOC_REF);
  return m ? m[1].trim() : null;
};

export default function VerificationHighCourt() {
  const location = useLocation();
  // Use explicit typing for useState
  const [applicationType, setApplicationType] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [affidavits, setAffidavits] = useState([]);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const query = decodeRef(location.pathname.replace(/^\/search\/?/, ""));

  useEffect(() => {
    // certified true copies (CTC module) have their own verification page
    const ctc = (query || "").replace(/\/true$/, "");
    if (/^CTC\d{8,}$/.test(ctc)) {
      navigate(`/verify-certified/${encodeURIComponent(ctc)}`, { replace: true });
      return;
    }
    if (query && !query.startsWith("VerificationHighCourt")) {
      setReferenceNumber(query);
      onPublicSearch(query, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const goToCaseSearch = (caseRef, fromQr) =>
    navigate(`/online-search/results?q=${encodeURIComponent(caseRef)}`, { replace: fromQr });

  const onPublicSearch = async (raw, fromQr = false) => {
    const qr = decodeRef(raw);
    const caseRef = caseRefFromDocRef(qr);
    setMessage("");
    setResult(null);
    setAffidavits([]);
    setLoading(true);
    try {
      const resp = await fetch(`${Constants.apiUrl}/report/document-verification`, {
        method: "POST",
        body: JSON.stringify({ documentId: qr }),
        headers: { "Content-Type": "application/json, text/plain" },
      });
      const data = await resp.json();
      setLoading(false);

      if (data.code === 0 && data.caseNumber) {
        // case document: search by its current suit number
        goToCaseSearch(data.caseNumber, fromQr);
      } else if (caseRef) {
        // case document whose case has no suit number yet (or the ref is not on record): search by the ref on the QR
        goToCaseSearch(caseRef, fromQr);
      } else if (data.code === 0 && data.doc) {
        // affidavit document
        setResult(data.doc);
        setAffidavits(data._docs || []);
      } else {
        setMessage(data.status || "No document was found with this reference number.");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      if (caseRef) goToCaseSearch(caseRef, fromQr);
      else setMessage("Unable to verify the document right now. Please try again.");
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Verifying:", { applicationType, referenceNumber });
    // In future: fetch verification result here (API call)

    // NOTE: You can add basic validation here
    if (!referenceNumber.trim()) {
      alert("Please  enter a Reference Number.");
      return;
    }

    // Logic to set a loading state or display results would go here
    onPublicSearch(referenceNumber);
  };

  return (
    <section className="bg-gray-50 dark:bg-gray-800 min-h-screen">
      <CompactHeader />
      {/* Header Section */}
      <section className="relative py-24 bg-hero-light-image dark:bg-hero-dark">
        {/* <div className="absolute inset-0 bg-black/20"></div> */}
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Verification – High Court
            </h1>
          </div>
        </div>
      </section>
      <div className="container mx-auto px-4 py-4 sm:px-6 max-w-6xl">
        {/* Verification Platform Card */}
        <div className="bg-white dark:bg-gray-700 shadow-lg rounded-md border border-gray-200 dark:border-gray-600">
          <div className="p-6">
            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center mb-6"
            >
              {/* Select Application Type */}
              {/* <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                  Select Application Type
                </label>
                <select
                  value={applicationType}
                  onChange={(e) => setApplicationType(e.target.value )} // Cast value to ApplicationType
                  className="w-full border border-gray-300 dark:border-gray-500 rounded-md px-4 py-2 text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Application Type</option>
                  <option value="Affidavit">Affidavit</option>
                  <option value="filing-details">External Affidavit</option>
                  <option value="Forms-Letters">Forms and Letters</option>
                  <option value="CTC">CTC</option>
                </select>
              </div>
 */}
              {/* Reference Number Input */}
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                  Enter Reference Number/ Affidavit ID
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="Enter Reference Number"
                    className="flex-grow border border-gray-300 dark:border-gray-500 rounded-l-md px-4 py-2 text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="submit"
                    className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 rounded-r-md transition-colors"
                  >
                    View
                  </button>
                </div>
              </div>
            </form>

            {/* Results Box */}
            <div className="border border-gray-300 dark:border-gray-600 rounded-md min-h-[200px] bg-white dark:bg-gray-800 p-4">
              {/* Placeholder for verification result */}
              {result == null && message && (
                <p className="text-red-600 dark:text-red-400">{message}</p>
              )}
              {result == null && !message && (
                <p className="text-gray-500 dark:text-gray-400 italic">
                  Verification results will appear here...
                </p>
              )}
              {result != null && (
                <p className="text-gray-500 dark:text-gray-400 italic">
                  Document with title <strong>{result.documentTitle}</strong>
                  {affidavits.length > 0 && (
                    <strong>Total document {affidavits.length}</strong>
                  )}
                  <p>Payment is required. Click to Pay to download</p>
                  <JisButton>Pay N1,500</JisButton>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <BackLoader open={loading} />
    </section>
  );
}
